/**
 * Field List Component
 * Displays a list of fields with nested dropdowns for relational fields
 * Based on reference: reference/app/src/components/v-field-list/
 */

import { useMemo, useState } from 'react';
import {
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search } from 'lucide-react';
import { useFieldTree, type FieldNode } from '@/hooks/useFieldTree';
import { cn } from '@/lib/utils';

interface FieldListProps {
    collection: string;
    disabledFields?: string[];
    onAdd: (fieldKey: string) => void;
    relationalFieldSelectable?: boolean;
    search?: string;
    onSearchChange?: (search: string) => void;
}

interface FieldListItemProps {
    node: FieldNode;
    onAdd: (fieldKey: string) => void;
    relationalFieldSelectable?: boolean;
    search?: string;
    level?: number;
}

function FieldListItem({ node, onAdd, relationalFieldSelectable = true, search, level = 0 }: FieldListItemProps) {
    const hasChildren = node.children && node.children.length > 0;
    const isRelational = !!node.relatedCollection;
    const canSelectDirectly = relationalFieldSelectable || !isRelational;

    // Check if field matches search
    const matchesSearch = useMemo(() => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            node.name.toLowerCase().includes(searchLower) ||
            node.field.toLowerCase().includes(searchLower) ||
            node.children?.some(child => 
                child.name.toLowerCase().includes(searchLower) ||
                child.field.toLowerCase().includes(searchLower)
            )
        );
    }, [node, search]);

    if (!matchesSearch) return null;

    // If it has children (relational field with nested fields), show as submenu
    if (hasChildren) {
        return (
            <DropdownMenuSub>
                <DropdownMenuSubTrigger
                    className={cn(
                        node.disabled && "opacity-50 cursor-not-allowed"
                    )}
                    disabled={node.disabled}
                >
                    <span className="flex-1">{node.name}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                    {/* Option to select the relational field itself - always show if selectable */}
                    {canSelectDirectly && (
                        <>
                            <DropdownMenuItem
                                onClick={() => onAdd(node.key)}
                                disabled={node.disabled}
                            >
                                {node.name}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )}
                    {/* Nested fields */}
                    {node.children?.map((child) => (
                        <FieldListItem
                            key={child.key}
                            node={child}
                            onAdd={onAdd}
                            relationalFieldSelectable={relationalFieldSelectable}
                            search={search}
                            level={level + 1}
                        />
                    ))}
                </DropdownMenuSubContent>
            </DropdownMenuSub>
        );
    }

    // Regular field - simple menu item
    return (
        <DropdownMenuItem
            onClick={() => onAdd(node.key)}
            disabled={node.disabled}
            className={cn(
                level > 0 && "pl-6"
            )}
        >
            {node.name}
        </DropdownMenuItem>
    );
}

export function FieldList({ 
    collection, 
    disabledFields = [], 
    onAdd, 
    relationalFieldSelectable = true,
    search: externalSearch,
    onSearchChange
}: FieldListProps) {
    const [internalSearch, setInternalSearch] = useState('');
    const search = externalSearch !== undefined ? externalSearch : internalSearch;
    const setSearch = onSearchChange || setInternalSearch;

    const { treeList } = useFieldTree({
        collection,
        disabledFields,
        includeRelations: true,
        includeAliasFields: false,
    });

    // Filter tree list based on search
    const filteredTreeList = useMemo(() => {
        if (!search) return treeList;

        const searchLower = search.toLowerCase();
        const filterNode = (node: FieldNode): FieldNode | null => {
            const matches = 
                node.name.toLowerCase().includes(searchLower) ||
                node.field.toLowerCase().includes(searchLower);

            const filteredChildren = node.children
                ?.map(filterNode)
                .filter((n): n is FieldNode => n !== null) || [];

            if (matches || filteredChildren.length > 0) {
                return {
                    ...node,
                    children: filteredChildren.length > 0 ? filteredChildren : node.children,
                };
            }

            return null;
        };

        return treeList.map(filterNode).filter((n): n is FieldNode => n !== null);
    }, [treeList, search]);

    const showSearch = treeList.length > 10;

    return (
        <div className="w-64">
            {showSearch && (
                <div className="p-2 border-b">
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search fields..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8 h-8"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
            <div className="p-1 max-h-[400px] overflow-y-auto">
                <Label className="text-xs text-muted-foreground px-2 py-1.5">Add Field</Label>
                {filteredTreeList.length > 0 ? (
                    <>
                        {filteredTreeList.map((node) => (
                            <FieldListItem
                                key={node.key}
                                node={node}
                                onAdd={onAdd}
                                relationalFieldSelectable={relationalFieldSelectable}
                                search={search}
                            />
                        ))}
                    </>
                ) : (
                    <p className="text-sm text-muted-foreground px-2 py-1.5">
                        {search ? 'No fields found' : 'All fields are visible'}
                    </p>
                )}
            </div>
        </div>
    );
}

